<template>
  <div
    id="settings-user"
    class="d-f jc-c"
    :class="forSubmitDemoModal ? 'w-100 m-0' : 'big-m-t big-m-b p'"
  >
    <div :class="forSubmitDemoModal ? 'w-100' : 'settings-outer'">
    <div :class="forSubmitDemoModal ? 'w-100' : 'small-wrap settings-form-col'">
      <!-- Profile Media card -->
      <div class="profile-media-card m-b">
        <!-- <span class="profile-media-label font-small tt-up mute">Profile Media</span> -->

        <div class="cover-wrap p-rel">
          <!-- Cover zone -->
          <div
            class="cover-zone p-rel pointer"
            :class="{ 'has-cover': coverImg }"
            @click="$refs.coverInput.click()"
          >
            <img v-if="coverImg" :src="coverImg" class="cover-zone__img" alt="" />

            <!-- Crop region guides (empty state) -->
            <div v-if="!coverImg" class="cover-guides">
              <div class="cover-guide cover-guide--left"></div>
              <div class="cover-guide cover-guide--mid">
                <span class="mobile-crop-badge font-small">Mobile Crop Area</span>
                <IconBase :w="15" :h="15"><IconUpload /></IconBase>
                <span class="font-small2">Upload Cover Image</span>
              </div>
              <div class="cover-guide cover-guide--right"></div>
            </div>

            <!-- Hover overlay (when image set) -->
            <div class="cover-zone__overlay d-f fd-c ai-c jc-c g-half">
              <IconBase :w="15" :h="15"><IconUpload /></IconBase>
              <span class="font-small2">Change Cover</span>
              <button class="secondary small" @click.stop="removeCover">Remove</button>
            </div>

            <input ref="coverInput" type="file" accept="image/*" class="d-n" @change="onCoverSelected" />
          </div>

          <!-- Avatar circle overlapping bottom center of cover -->
          <div
            class="avatar-zone pointer"
            :class="{ 'has-avatar': profileImgPreview }"
            @click="$refs.avatarInput.click()"
          >
            <img v-if="profileImgPreview" :src="profileImgPreview" class="avatar-zone__img" alt="" />
            <div class="avatar-zone__overlay d-f ai-c jc-c">
              <IconBase :w="15" :h="15"><IconUpload /></IconBase>
            </div>
            <input ref="avatarInput" type="file" accept="image/*" class="d-n" @change="onAvatarSelected" />
          </div>
        </div>

        <!-- Dimension hints -->
        <div class="profile-media-hints d-f jc-sb">
          <span class="d-f ai-c g-half">
            <span class="font-small2 mute">Cover - 1500×500px</span>
            <span v-if="coverImg" class="font-small2 text-link mute" @click="removeCover">Remove</span>
          </span>
          <span class="d-f ai-c g-half">
            <span class="font-small2 mute">Avatar - 400×400px</span>
            <span v-if="profileImgPreview" class="font-small text-link mute" @click="removeProfilePhoto">Remove</span>
          </span>
        </div>

        <!-- Logotype -->
        <div class="logo-upload-row d-f fd-c ai-c g-half">
          <div
            class="logo-zone p-rel pointer"
            :class="{ 'has-logo': logoImg }"
            @click="$refs.logoInput.click()"
          >
            <img v-if="logoImg" :src="logoImg" class="logo-zone__img" alt="" />
            <div v-else class="logo-zone__empty d-f fd-c ai-c jc-c g-half">
              <IconBase :w="15" :h="15"><IconUpload /></IconBase>
              <span class="font-small2">Upload Logotype</span>
              <span class="font-small2 mute">( PNG or SVG )</span>
            </div>
            <div class="logo-zone__overlay d-f fd-c ai-c jc-c g-half">
              <IconBase :w="15" :h="15"><IconUpload /></IconBase>
              <span class="font-small">Change</span>
            </div>
            <input ref="logoInput" type="file" accept="image/png,image/svg+xml" class="d-n" @change="onLogoSelected" />
          </div>
          <p v-if="logoImg" class="text-link font-small tt-up m-0" @click.stop="removeLogo">Remove</p>
        </div>
      </div>

      <ImageCropper
        :src="coverCroppingFile"
        :circle="false"
        :aspect-ratio="3"
        :max-width="1500"
        :max-height="500"
        :quality="0.85"
        @save="onCoverCropped"
        @cancel="coverCroppingFile = null"
      />
      <ImageCropper
        :src="avatarCroppingFile"
        :circle="true"
        :max-size="400"
        :quality="0.9"
        @save="onAvatarCropped"
        @cancel="avatarCroppingFile = null"
      />

      <form class="d-f fd-c m-b-children" @submit.prevent>
        <div class="input-container">
          <label class="label" for="username" :class="{ error: usernameError }">Username</label>
          <input
            type="text"
            id="username"
            name="username"
            :value="getUsername"
            @input="changeUsername($event)"
            placeholder="Username"
            :class="{ error: usernameError }"
          />
        </div>
        <p v-if="usernameError" class="as-s font-small error red">{{ usernameError }}</p>
        <div class="d-f jc-fe" v-show="showSaveUsername">
          <button type="button" class="m-r" @click="cancelUsernameChange">Cancel</button>
          <button type="button" @click="saveUsername">Save Username</button>
        </div>

        <SettingsSection title="Location" :status="locationStatus">
          <template #icon><IconBase :w="15" :h="15"><IconPin /></IconBase></template>
          <LocationInput
            v-if="getUserSettings && renderComponent"
            :default-location="defaultLocations"
            mode="multi"
            @changed="locationChanged"
          />
          <div class="d-f jc-fe g" v-if="locationDirty">
            <button class="m-r" @click.prevent="resetLocationChanges">Cancel</button>
            <button @click.prevent="saveLocation" :disabled="!canSaveLocation">Save location</button>
          </div>
        </SettingsSection>

        <GenreSelect
          v-if="getUserSettings && getUser.data?.uid"
          :key="getUser.data.uid"
          :value="getUserSettings.genres"
          :userId="getUser.data.uid"
        />

        <div class="ranking-explanation w-100 d-f fd-c ai-c g m-b">
          <div class="d-f fd-c bc-black br-input w-100 bs-bb p-md m-0">
            <p class="m-0 d-f fd-c jc-c">
              <span class="tt-up font-small2 m-b-half"><strong>How the ranking works</strong></span>
            </p>
            <p class="m-0 mute font-small2 p-b-sm" style="max-width: 560px;">
              Your rank is based on a ratio of interactions (likes, comments, playlist adds) divided by unique plays. You appear in charts once you have more than 3 unique plays. Until then, you stay in Unranked chart.
            </p>
          </div>
        </div>

        <SettingsSection title="Events" :status="eventsStatus">
          <template #icon><IconBase :w="15" :h="15"><IconCalendar /></IconBase></template>
          <EventsEditor v-if="getUser.data && getUser.data.uid" :user-id="getUser.data.uid" @count="eventsCount = $event" />
        </SettingsSection>

        

        <SettingsSection title="Social Handles" :status="profileLinksStatus">
          <template #icon><IconBase :w="15" :h="15"><IconShare /></IconBase></template>
          <SettingsLinks />
        </SettingsSection>

        <SettingsSection title="Links Section" :status="buttonLinksStatus">
          <template #icon><IconBase :w="15" :h="15"><IconLink /></IconBase></template>
          <SettingsLinksSection v-if="getUser.data && getUser.data.uid" :user-id="getUser.data.uid" @count="buttonLinksCount = $event" />
        </SettingsSection>

        <SettingsSection title="Demo Dropbox" :status="demoDropboxStatus">
          <template #icon><IconBase :w="15" :h="15"><IconSend /></IconBase></template>
          <SettingsLabelDropboxDropdown
            :checked="acceptingSubmissions"
            title="Submit Demo Button"
            subtitle=""
            :show-checkbox="true"
            @on-change="toggleAcceptingSubmissions"
          >
            <div class="input-container w-100 desc-field desc-textarea-wrap">
              <label class="label" for="settings-user-demo-criteria"
                >Define criteria for demo assessment...</label
              >
              <textarea
                id="settings-user-demo-criteria"
                class="desc-textarea"
                maxlength="800"
                rows="3"
                placeholder="Define criteria for demo assessment..."
                v-model="demoCriteria"
                @blur="saveDemoCriteria"
              />
            </div>
          </SettingsLabelDropboxDropdown>
        </SettingsSection>

        <SettingsSection title="Account" :status="accountStatus">
          <template #icon><IconBase :w="15" :h="15"><IconUser /></IconBase></template>
          <div
            ref="optOut"
            id="opt-out"
            class="toggle-wrapper"
            :class="{ 'b-brand': $route.hash.includes('opt-out') }"
            @click.prevent="updateMailSub(mailSubscriptionStatus)"
          >
            <label class="label flex-1" for="toggle-mail-sub"
              >Opt-out of Profile Summary Emails</label
            >
            <ToggleSwitch :value="!mailSubscriptionStatus" />
          </div>
          <div class="input-container">
            <label class="label" for="email">Email</label>
            <input type="text" id="email" name="email" v-model="email" placeholder="Email" disabled />
          </div>
          <div class="input-container">
            <label class="label" for="member">Member</label>
            <input type="text" id="member" name="member" v-model="member" placeholder="Member" disabled />
          </div>
        </SettingsSection>

        <router-link to="/my_music" class="button w-100 as-s tt-up d-f ai-c jc-c g-half">
          Add Tracks
          <IconBase :w="15" :h="15"><IconArrowRight /></IconBase>
        </router-link>


      </form>

      <div
        v-if="forSubmitDemoModal"
        class="save-buttons d-f jc-c ai-c p-f b-0 l-0 bc-brand w-100 m-b-0 p bs-bb"
      >
        <button
          v-if="forSubmitDemoModal"
          class="d-f ai-c jc-c g"
          :disabled="!submitBtnEnabled"
          @click.prevent="$emit('submitDemo')"
        >
          {{ savingDemo ? "Submitting Your Demo..." : "Send demo" }}
          <IconBase
            w="15"
            h="15"
            color="black"
            viewBox="0 0 15 15"
            icon-name="submit"
          >
            <IconSend />
          </IconBase>
        </button>
      </div>

      <div class="label-divider p-rel w-100">
        <span class="w-100 b-bottom p-a pos-center"></span>
      </div>

      <!-- Logout, Delete account -->
      <div class="d-f fd-c flex-1 jc-c" v-if="!forSubmitDemoModal">
        <div
          class="d-f flex-1 ai-c jc-c p"
          v-if="!showDeleteOption && !showChangePassword"
        >
          <div class="text-link" @click="showChangePassword = true">
            Change Password
          </div>
          <span class="m-l m-r grey o-5">|</span>
          <div class="text-link" @click="logout()">Log Out</div>
          <span class="m-l m-r grey o-5">|</span>
          <div class="text-link" @click="showDeleteOption = true">
            Delete Account
          </div>
        </div>
        <!-- Change Password -->
        <div v-if="!forSubmitDemoModal" class="d-f fd-c flex-1 jc-c">
          <div class="w-100 bs-bb d-f ai-c jc-c" v-if="showChangePassword">
            <div class="w-100 d-f fd-c g">
              <div class="input-container w-100">
                <label class="label" for="confirm-pass">Current Password</label>
                <input
                  type="password"
                  id="confirm-pass"
                  name="confirm-pass"
                  v-model="currentPassword"
                  placeholder="Current Password"
                />
              </div>
              <div class="input-container w-100">
                <label
                  class="label"
                  for="change-pass"
                  :class="{ error: passwordMismatch }"
                  >New Password</label
                >
                <input
                  type="password"
                  id="change-pass"
                  name="change-pass"
                  v-model="newPassword"
                  placeholder="New Password"
                  :class="{ error: passwordMismatch }"
                />
              </div>
              <div class="input-container w-100">
                <label
                  class="label"
                  for="confirm-new-pass"
                  :class="{ error: passwordMismatch }"
                  >Confirm New Password</label
                >
                <input
                  type="password"
                  id="confirm-new-pass"
                  name="confirm-new-pass"
                  v-model="confirmNewPassword"
                  placeholder="Confirm New Password"
                  :class="{ error: passwordMismatch }"
                />
              </div>
              <p v-if="passwordMismatch" class="as-s p-l error">
                Passwords do not match
              </p>
              <div class="d-f jc-c ai-c g p">
                <button
                  @click.prevent="savePassword"
                  :disabled="passwordMismatch"
                >
                  Save Password
                </button>
                <button class="secondary" @click="resetPasswordForm">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
        <!-- Delete account -->
        <div class="delete-account w-100 bs-bb d-f ai-c jc-c">
          <div class="w-100 d-f fd-c g" v-if="showDeleteOption">
            <p class="">Are you sure you want to delete this account?</p>
            <div class="d-f jc-c ai-c g p">
              <button class="" @click="deleteAccount">Confirm deletion</button>
              <button class="secondary" @click="showDeleteOption = false">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <ProfilePreview
      v-if="!forSubmitDemoModal"
      :username-url="getUserSettings && getUserSettings.usernameUrl"
      :refresh-trigger="previewRefresh"
    />

    </div>
  </div>
</template>
<script>
import { auth, fb, functions, rtdb, storage } from "@/db.js";
import { sanitizeNameFrom } from "@/common";
import { mapGetters } from "vuex";
import SettingsLinksSection from "@/components/settings/SettingsLinksSection.vue";
import SettingsLinks from "@/components/settings/SettingsLinks.vue";
import EventsEditor from "@/components/settings/EventsEditor.vue";
import IconSend from "@/assets/icons/IconSend.vue";
import IconBase from "@/assets/icons/IconBase.vue";
import ProfilePreview from "@/components/settings/ProfilePreview.vue";
import SettingsSection from "@/components/settings/SettingsSection.vue";
import LocationInput from "@/components/misc/LocationInput.vue";
import GenreSelect from "@/components/settings/GenreSelect.vue";
import ToggleSwitch from "@/components/misc/ToggleSwitch.vue";
import SettingsLabelDropboxDropdown from "@/components/settings/SettingsLabelDropboxDropdown.vue";
import ImageCropper from "@/components/misc/ImageCropper.vue";
import IconUpload from "@/assets/icons/IconUpload.vue";
import IconMusic from "@/assets/icons/IconMusic.vue";
import IconPlus from "@/assets/icons/IconPlus.vue";
import IconCalendar from "@/assets/icons/IconCalendar.vue";
import IconShare from "@/assets/icons/IconShare.vue";
import IconLink from "@/assets/icons/IconLink.vue";
import IconUser from "@/assets/icons/IconUser.vue";
import { delay } from "lodash";
import IconPin from "@/assets/icons/IconPin.vue";
import IconArrowRight from "@/assets/icons/IconArrowRight.vue";

export default {
  name: "SettingsUser",
  components: {
    ToggleSwitch,
    LocationInput,
    IconBase,
    IconSend,
    IconUpload,
    SettingsLinksSection,
    SettingsLinks,
    GenreSelect,
    SettingsLabelDropboxDropdown,
    EventsEditor,
    ProfilePreview,
    SettingsSection,
    ImageCropper,
    IconMusic,
    IconPlus,
    IconPin,
    IconCalendar,
    IconShare,
    IconLink,
    IconUser,
    IconArrowRight,
  },
  props: {
    forSubmitDemoModal: {
      type: Boolean,
      default: false,
    },
    submitBtnEnabled: {
      type: Boolean,
      default: false,
    },
    savingDemo: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      newUsername: null,
      showDeleteOption: false,
      showChangePassword: false,
      deleteAccPassword: "",
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
      showSaveUsername: null,
      usernameError: "",
      renderComponent: true,
      pendingLocation: null,
      locationDirty: false,
      acceptingSubmissions: this.$store?.getters?.getUserSettings?.acceptingSubmissions ?? false,
      demoCriteria: this.$store?.getters?.getUserSettings?.demoCriteria ?? "",
      eventsCount: null,
      buttonLinksCount: null,
      previewRefresh: 0,
      coverImg: this.$store?.getters?.getUserSettings?.coverImg ?? null,
      coverCroppingFile: null,
      profileImgPreview: this.$store?.getters?.getUserSettings?.profileImg ?? null,
      avatarCroppingFile: null,
      logoImg: this.$store?.getters?.getUserSettings?.logoImg ?? null,
    };
  },
  watch: {
    userSettings(newVal, oldVal) {
      if (oldVal && newVal) this.previewRefresh++;
      this.forceRerender();

      if (newVal) {
        this.acceptingSubmissions = newVal.acceptingSubmissions ?? false;
        this.demoCriteria = newVal.demoCriteria ?? "";
        this.coverImg = newVal.coverImg ?? null;
        this.profileImgPreview = newVal.profileImg ?? null;
        this.logoImg = newVal.logoImg ?? null;
      }

      if (!oldVal && newVal && this.$route.hash.includes("opt-out")) {
        delay(() => {
          this.$refs.optOut.scrollIntoView();
        }, 1000);
      }
    },
  },
  computed: {
    ...mapGetters(["getUser", "getUserSettings"]),
    userSettings() {
      return this.getUserSettings;
    },
    member() {
      return "Member#" + this.getUserSettings.member;
    },
    email() {
      if (this.getUser.loggedIn) {
        return this.getUser.data.email;
      } else {
        return;
      }
    },
    getUsername() {
      return this.getUserSettings.username;
    },
    defaultLocations() {
      const locs = this.getUserSettings?.locations ?? {};
      return {
        loc1: locs.loc1 ?? "",
        loc2: locs.loc2 ?? "",
        loc3: locs.loc3 ?? "",
      };
    },
    passwordMismatch() {
      return (
        this.newPassword &&
        this.confirmNewPassword &&
        this.newPassword !== this.confirmNewPassword
      );
    },
    canSaveLocation() {
      if (!this.pendingLocation) return false;
      const hasSelection = ["loc1", "loc2", "loc3"].some(
        (key) => this.pendingLocation.details?.[key]
      );
      const hasValue = ["loc1", "loc2", "loc3"].some(
        (key) => this.pendingLocation[key]
      );
      const hasCleared =
        (this.pendingLocation.clearedKeys?.length ?? 0) > 0 ||
        ["loc1", "loc2", "loc3"].some(
          (key) =>
            this.pendingLocation[key] === "" &&
            (this.getUserSettings?.locations?.[key] ?? "") !== ""
        );
      return (hasSelection && hasValue) || hasCleared;
    },
    mailSubscriptionStatus() {
      return this.userSettings?.mailSubscriptionStatus ?? true;
    },
    locationStatus() {
      const locs = this.getUserSettings?.locations;
      const active = ['loc1', 'loc2', 'loc3'].filter(k => locs?.[k]);
      if (!active.length) return 'not set';
      return active.map(k => locs[k]).join(', ');
    },
    eventsStatus() {
      if (this.eventsCount === null) return null;
      return this.eventsCount > 0 ? `(${this.eventsCount})` : 'Not set';
    },
    profileLinksStatus() {
      if (!this.getUserSettings) return null;
      const count = Object.keys(this.getUserSettings?.links || {}).length;
      return count > 0 ? `(${count})` : 'Not set';
    },
    buttonLinksStatus() {
      if (this.buttonLinksCount === null) return null;
      return this.buttonLinksCount > 0 ? `(${this.buttonLinksCount})` : 'Not set';
    },
    demoDropboxStatus() {
      return this.acceptingSubmissions ? 'On' : 'Off';
    },
    accountStatus() {
      return this.email || null;
    },
  },
  methods: {
    forceRerender() {
      // Remove my-component from the DOM
      this.renderComponent = false;

      this.$nextTick(() => {
        // Add the component back in
        this.renderComponent = true;
      });
    },
    changeUsername(event) {
      // Clear error when user types
      this.usernameError = "";
      
      if (event.target.value !== this.getUserSettings.username) {
        // console.log(event.target.value);
        this.newUsername = event.target.value;
        this.showSaveUsername = true;
      } else {
        this.newUsername = null;
        this.showSaveUsername = false;
      }
    },
    cancelUsernameChange() {
      this.showSaveUsername = false;
      this.newUsername = null;
      this.usernameError = "";
    },
    async saveUsername() {
      const currentUsername = this.getUsername;
      const newUsername = this.newUsername;
      if (newUsername == null || newUsername === currentUsername) return;

      const uid = this.getUser.data.uid;
      const usernameToCheck = sanitizeNameFrom(newUsername).toLowerCase();
      const encodedUsername = encodeURIComponent(usernameToCheck).replace(
        /\./g,
        "%2E"
      );

      try {
        const usernameCheck = await rtdb
          .ref("usernames")
          .child(encodedUsername)
          .once("value");

        if (usernameCheck.exists() && usernameCheck.val() !== uid) {
          this.usernameError = "Oops! That username is taken";
          return;
        }

        const usernamesRef = rtdb.ref("usernames");
        const userSettingsRef = rtdb.ref("userSettings").child(uid);
        const oldUsernameUrl = this.getUserSettings.usernameUrl;

        await usernamesRef.update({ [encodedUsername]: uid });
        if (oldUsernameUrl && oldUsernameUrl !== encodedUsername) {
          await usernamesRef.child(oldUsernameUrl).remove();
        }
        await userSettingsRef.update({
          username: newUsername,
          usernameUrl: usernameToCheck,
        });

        this.newUsername = null;
        this.showSaveUsername = false;
        this.usernameError = "";
        this.$store.dispatch("togglePopMess", "Username saved!");
      } catch (error) {
        console.error("Error saving username:", error);
        this.usernameError = "Failed to save username. Please try again.";
      }
    },
    resetPasswordForm() {
      this.newPassword = "";
      this.confirmNewPassword = "";
      this.currentPassword = "";
      this.showChangePassword = false;
    },
    savePassword() {
      if (this.passwordMismatch) {
        alert("Passwords do not match");
        return;
      }
      if (this.newPassword.length > 1) {
        const user = auth.currentUser;
        const newPass = this.newPassword;
        const credential = fb.auth.EmailAuthProvider.credential(
          user.email,
          this.currentPassword
        );
        // Prompt the user to re-provide their sign-in credentials
        user
          .reauthenticateWithCredential(credential)
          .then(() => {
            console.log("User re-authenticated.");
            user.updatePassword(newPass).then(() => {
              console.log("Update password successful");
              this.resetPasswordForm();
              this.$router.go();
            });
          })
          .catch((error) => {
            alert(error.message);
          });
      } else {
        console.log("Nothing to save here.");
      }
    },
    async deleteAccount() {
      const user = auth.currentUser;
      if (!user) {
        alert("No user logged in");
        return;
      }

      // Check if user signed in with Google, Apple or email/password
      const providerIds = new Set(
        (user.providerData ?? []).map((p) => p.providerId)
      );
      const isGoogleUser = providerIds.has("google.com");
      const isAppleUser = providerIds.has("apple.com");

      let reauthPromise;

      if (isGoogleUser) {
        const provider = new fb.auth.GoogleAuthProvider();
        reauthPromise = user.reauthenticateWithPopup(provider);
      } else if (isAppleUser) {
        const provider = new fb.auth.OAuthProvider("apple.com");
        reauthPromise = user.reauthenticateWithPopup(provider);
      } else {
        const password = prompt(
          "Please enter your password to confirm account deletion:"
        );
        if (!password) {
          return;
        }
        const credential = fb.auth.EmailAuthProvider.credential(
          user.email,
          password
        );
        reauthPromise = user.reauthenticateWithCredential(credential);
      }

      try {
        await reauthPromise;
        console.log("User re-authenticated.");

        const deleteMyAccount = functions.httpsCallable("deleteMyAccount");
        await deleteMyAccount();
        console.log("User DELETED.");
        this.$router.push("/");
        location.reload();
      } catch (err) {
        console.error(err);
        alert((err && err.message) || "Authentication failed. Please try again.");
      }
    },
    logout() {
      return this.$store.dispatch("logout");
    },
    async removeProfilePhoto() {
      this.profileImgPreview = null;
      await this.profileImageChanged(null);
    },
    onAvatarSelected() {
      const file = this.$refs.avatarInput?.files?.[0];
      if (file) this.avatarCroppingFile = file;
    },
    async onAvatarCropped(dataURL) {
      this.avatarCroppingFile = null;
      this.profileImgPreview = dataURL;
      await this.profileImageChanged(dataURL);
    },
    onCoverSelected() {
      const file = this.$refs.coverInput?.files?.[0];
      if (file) this.coverCroppingFile = file;
    },
    async onCoverCropped(dataURL) {
      this.coverCroppingFile = null;
      const uid = this.getUser.data.uid;
      const url = await storage
        .ref(`userSettings/${uid}/coverimages/CoverImg`)
        .putString(dataURL, 'data_url')
        .then(s => s.ref.getDownloadURL());
      await rtdb.ref('userSettings').child(uid).update({ coverImg: url });
      this.coverImg = url;
      this.$store.dispatch('togglePopMess', 'Cover saved!');
    },
    async removeCover() {
      const uid = this.getUser.data.uid;
      await rtdb.ref('userSettings').child(uid).update({ coverImg: null });
      this.coverImg = null;
      this.$store.dispatch('togglePopMess', 'Cover removed');
    },
    async onLogoSelected() {
      const file = this.$refs.logoInput?.files?.[0];
      if (!file) return;
      const uid = this.getUser.data.uid;
      const ext = file.name.split('.').pop().toLowerCase();
      const url = await storage
        .ref(`userSettings/${uid}/logo/LogoImg.${ext}`)
        .put(file)
        .then(s => s.ref.getDownloadURL());
      await rtdb.ref('userSettings').child(uid).update({ logoImg: url });
      this.logoImg = url;
      this.$store.dispatch('togglePopMess', 'Logotype saved!');
    },
    async removeLogo() {
      const uid = this.getUser.data.uid;
      await rtdb.ref('userSettings').child(uid).update({ logoImg: null });
      this.logoImg = null;
      this.$store.dispatch('togglePopMess', 'Logotype removed');
    },
    async profileImageChanged(imageData) {
      const usersRef = rtdb.ref("userSettings").child(this.getUser.data.uid);
      const storageRef = storage.ref(
        `userSettings/${this.getUser.data.uid}/profileimages/ProfileImg`
      );

      let url;

      if (imageData) {
        url = await storageRef
          .putString(imageData, "data_url")
          .then((snapshot) => snapshot.ref.getDownloadURL());
      }

      await usersRef.update({
        profileImg: url || null, // Remove image if not set
      });

      this.$store.dispatch("togglePopMess", "Changes saved!");
      console.log("Profile Image changed");
    },
    locationChanged(locationData) {
      if (!locationData) return;
      const hasValidSelection = ["loc1", "loc2", "loc3"].some(
        (key) => locationData.details?.[key]
      );
      const hasCleared = (locationData.clearedKeys?.length ?? 0) > 0;
      if (!hasValidSelection && !hasCleared) {
        return;
      }
      this.pendingLocation = locationData;
      this.locationDirty = true;
    },
    saveLocation() {
      if (!this.canSaveLocation) return;
      const locationData = this.pendingLocation;
      const locations = { loc0: "World" };
      ["loc1", "loc2", "loc3"].forEach((key) => {
        if (locationData[key]) {
          locations[key] = locationData[key];
        }
      });

      const detailSource =
        locationData.details?.loc1 ||
        locationData.details?.loc2 ||
        locationData.details?.loc3 ||
        null;

      const updates = {
        locations,
      };

      if (detailSource?.lat && detailSource?.lng) {
        updates.locationInfo = {
          placeRef: detailSource.placeRef ?? null,
          lat: detailSource.lat,
          lng: detailSource.lng,
        };
      }

      rtdb
        .ref("userSettings")
        .child(this.getUser.data.uid)
        .update(updates)
        .then(() => {
          this.$store.dispatch("togglePopMess", "Changes saved!");
          console.log("Rank area in userSettings updated");
          alert("NOTE: The new rank location will update after 1 hour.");
          this.pendingLocation = null;
          this.locationDirty = false;
          this.forceRerender();
        });
    },
    resetLocationChanges() {
      this.pendingLocation = null;
      this.locationDirty = false;
      this.forceRerender();
    },
    toggleAcceptingSubmissions(v) {
      this.acceptingSubmissions = v;
      rtdb
        .ref("userSettings")
        .child(this.getUser.data.uid)
        .child("acceptingSubmissions")
        .set(v)
        .then(() => {
          this.$store.dispatch("togglePopMess", "Changes saved!");
        });
    },
    saveDemoCriteria() {
      rtdb
        .ref("userSettings")
        .child(this.getUser.data.uid)
        .child("demoCriteria")
        .set(this.demoCriteria || null)
        .then(() => {
          this.$store.dispatch("togglePopMess", "Changes saved!");
        });
    },
    updateMailSub(value) {
      rtdb
        .ref("userSettings")
        .child(this.getUser.data.uid)
        .child("mailSubscriptionStatus")
        .set(!value)
        .then(() => {
          this.$store.dispatch("togglePopMess", "Changes saved!");
        });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/scss/_variables.scss";
@import "@/scss/_classes.scss";
@import "@/scss/_mixins.scss";

.settings-outer {
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: $mg-desktop-2xl;
  justify-content: center;
}

.settings-form-col {
  flex: 1;
  min-width: 0;
}

.profile-media-card {
  border: $border-small solid $line-color;
  border-radius: $border-radius-input;
  padding: $mg-desktop;
}

.profile-media-label {
  display: block;
  letter-spacing: 0.08em;
  margin-bottom: $mg-desktop-sm;
}

.cover-wrap {
  position: relative;
  margin-bottom: calc(44px + #{$mg-desktop-sm});
}

.cover-zone {
  height: 160px;
  border-radius: calc(#{$border-radius-input} - 2px);
  overflow: hidden;
  background-color: rgba(255, 255, 255, 0.04);
  border: $border-small dashed $line-color;
  position: relative;

  &__img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &__overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    color: rgba(255, 255, 255, 0.85);
    transition: opacity 0.2s ease;
    pointer-events: none;
    opacity: 0;

    button { pointer-events: auto; }
  }

  &:not(.has-cover) &__overlay { opacity: 0; pointer-events: none; }
  &.has-cover:hover &__overlay { opacity: 1; pointer-events: auto; }
}

.cover-guides {
  position: absolute;
  inset: 0;
  display: flex;
  color: rgba(255, 255, 255, 0.45);
}

.cover-guide {
  height: 100%;

  &--left  { flex: 0 0 27%; border-right: $border-small dashed $line-color; }
  &--right { flex: 0 0 27%; border-left: $border-small dashed $line-color; }

  &--mid {
    flex: 1;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
  }
}

.mobile-crop-badge {
  position: absolute;
  top: $mg-desktop-sm;
  background-color: rgba(0, 0, 0, 0.75);
  border: $border-small solid $line-color;
  border-radius: 4px;
  padding: 2px 8px;
  color: rgba(255, 255, 255, 0.7);
  letter-spacing: 0.02em;
}

.avatar-zone {
  position: absolute;
  top: 160px;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 88px;
  height: 88px;
  border-radius: 50%;
  overflow: hidden;
  background-color: rgba(255, 255, 255, 0.08);
  border: $border-small solid $line-color;
  cursor: pointer;

  &__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &__overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.55);
    color: rgba(255, 255, 255, 0.85);
    transition: opacity 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &.has-avatar &__overlay { opacity: 0; }
  &.has-avatar:hover &__overlay { opacity: 1; }
}

.profile-media-hints {
  margin-top: $mg-desktop-sm;
}

.logo-upload-row {
  margin-top: $mg-desktop;
  padding-top: $mg-desktop;
  border-top: $border-small solid $line-color;
}

.logo-zone {
  width: 100%;
  height: 90px;
  border: $border-small dashed $line-color;
  border-radius: calc(#{$border-radius-input} - 2px);
  background-color: rgba(255, 255, 255, 0.04);
  overflow: hidden;

  &__img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
    padding: 6px;
    box-sizing: border-box;
  }

  &__empty {
    position: absolute;
    inset: 0;
    color: rgba(255, 255, 255, 0.45);
  }

  &__overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    color: rgba(255, 255, 255, 0.85);
    opacity: 0;
    transition: opacity 0.2s ease;
    pointer-events: none;
  }

  &.has-logo &__empty { display: none; }
  &.has-logo:hover &__overlay { opacity: 1; }
}

</style>
